import React, {useEffect, useState} from 'react';
import {Button, Card, Collapse, Input, Pagination, Progress, Space, Spin, Tag, Typography} from "antd";
import {useApiSearchAdminPollResultQuestion} from "@/api/survey";
import AdminLayout from "@/components/layout/admin/adminLayout";
import {URLS} from "@/lib/urls";
import {EditOutlined, SearchOutlined} from "@ant-design/icons";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {Link} from "react-router-dom";
import {sprintf} from "@/lib/utils/sprintf";
import toFaNumber from "@/lib/utils/number";

function AdminSurveyResultListPage() {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [inputSearchValue, setInputSearchValue] = useState('');
    const [searchInputLoading, setSearchInputLoading] = useState(false);

    const {data: surveyResults, isLoading} = useApiSearchAdminPollResultQuestion({
        page: currentPage,
        search: searchText
    });


    const myBreadCrumb = [
        {
            path: URLS.homepage,
            title: "خانه"
        },
        {
            path: URLS.admin.survey.dashboard,
            title: "نظرسنجی"
        },
    ];

    useEffect(() => {
        setSearchInputLoading(true);
        const delaySearch = setTimeout(() => {
            setSearchText(inputSearchValue);
            setCurrentPage(1);
            setSearchInputLoading(false);
        }, 1000);

        return () => clearTimeout(delaySearch);
    }, [inputSearchValue]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const getQuestionTypeText = (type) => {
        switch (type) {
            case 'single_choice':
                return 'تک انتخابی';
            case 'multiple_choice':
                return 'چند انتخابی';
            case 'text':
                return 'تشریحی';
            default:
                return type;
        }
    };

    const renderChart = (question) => {
        if (question.type === 'text') return null;
        if (!question.answer_details_count) return null;
        const options = {
            chart: {
                type: 'column',
                height: 200,
                style: {
                    fontFamily: 'IRANSansX'
                }
            },
            title: {
                text: null
            },
            xAxis: {
                categories: question.options.map(opt => opt?.title),
                labels: {
                    style: {
                        fontFamily: 'IRANSansX'
                    }
                }
            },
            yAxis: {
                title: {
                    text: 'تعداد پاسخ‌ها',
                    style: {
                        fontFamily: 'IRANSansX'
                    }
                },
                min: 0,
                tickInterval: 1
            },
            legend: {
                enabled: false
            },
            credits: {
                enabled: false
            },
            plotOptions: {
                column: {
                    borderRadius: 5,
                    dataLabels: {
                        enabled: true,
                        style: {
                            fontFamily: 'IRANSansX'
                        }
                    }
                }
            },
            series: [{
                name: 'تعداد پاسخ‌ها',
                data: question.options.map(opt => ({
                    name: opt?.title,
                    y: opt?.answer_details_count,
                    color: 'rgba(54, 162, 235, 0.8)'
                }))
            }]
        };

        return (
            <div>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={options}
                />
            </div>
        );
    };

    const renderQuestion = (question) => {
        return (
            <Card key={question?.id} className="mb-4">
                <Space direction="vertical" style={{width: '100%'}}>
                    <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                            <Typography.Text type="secondary">
                                {question?.survey?.title}
                            </Typography.Text>
                            <Tag color={question?.survey?.status === 'published' ? 'green' : 'volcano'}>
                                {question?.survey?.status === 'published' ? 'منتشر شده' : 'پیش نویس'}
                            </Tag>
                            <Link to={sprintf(URLS.admin.survey.edit, {id: question?.survey?.id})}>
                                <Button type="" icon={<EditOutlined/>} size="small">

                                </Button>
                            </Link>
                        </div>
                    </div>
                    <Typography.Title level={5}>{question.title}</Typography.Title>
                    <div className="flex items-center gap-2 mb-2">
                        <Tag color="blue">{getQuestionTypeText(question?.type)}</Tag>
                        <Typography.Text type="secondary">
                            تعداد کل پاسخ‌ها: {toFaNumber(question.answer_details_count)}
                        </Typography.Text>
                    </div>


                    {question?.type === 'text' && question?.answer_details_count ? (
                        <Collapse
                            ghost
                            items={[{
                                key: '1',
                                label: 'مشاهده پاسخ‌ها',
                                children: (
                                    <div className="p-4 bg-gray-50 rounded">
                                        {question.answer_details?.map((answer, index) => (
                                            <div key={index} className="mb-2 p-2 bg-white rounded">
                                                {answer?.answer}
                                            </div>
                                        ))}
                                    </div>
                                ),
                            }]}
                        />
                    ) : (
                        <>
                            {renderChart(question)}
                            {(question?.answer_details_count) ?
                                <div className="mt-4">
                                    {question.options.map(option => (
                                        <div key={option?.id} className="mb-2">
                                            <div className="flex justify-between mb-1">
                                                <span>{option?.title}</span>
                                                <span>{option?.answer_details_count} پاسخ</span>
                                            </div>
                                            <Progress
                                                percent={Math.round((option?.answer_details_count / question?.answer_details_count) * 100)}
                                                size="small"
                                            />
                                        </div>
                                    ))}
                                </div>
                                : ''}
                        </>
                    )}
                </Space>
            </Card>
        );
    };

    return (
        <AdminLayout pageTitle={'نتایج نظرسنجی'} breadCrumb={myBreadCrumb}>
            <div className="p-4">
                <div className="mb-4">
                    <Input
                        placeholder="جستجو در سوالات..."
                        allowClear
                        suffix={searchInputLoading ? <Spin size="small"/> : <SearchOutlined/>}
                        size="large"
                        onChange={(e) => setInputSearchValue(e.target.value)}
                        value={inputSearchValue}
                        className="max-w-md"
                    />
                </div>

                {isLoading ? (
                    <div>در حال بارگذاری...</div>
                ) : (
                    <div>
                        {surveyResults?.data?.data?.map(question => renderQuestion(question))}

                        {surveyResults?.meta && (
                            <div className="mt-4 flex justify-center">
                                <Pagination
                                    current={surveyResults.meta.current_page}
                                    total={surveyResults.meta.total}
                                    pageSize={surveyResults.meta.per_page}
                                    onChange={handlePageChange}
                                    showSizeChanger={false}
                                    showTotal={(total, range) => `${range[0]}-${range[1]} از ${total} مورد`}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

export default AdminSurveyResultListPage;


